import * as geometric from 'geometric'

import { DeviceType } from '@/graphql/generated/schemas'

import { DeviceI, Point, Size } from '../types/types'
import { translateAngleTo360Range } from './chart'

/** A companion device and the position, and where applicable the aim, it should be placed at. */
export interface CompanionPlacementI {
  deviceId: string
  x: number
  y: number
  /** Bearing, set only for a companion camera turned to face the device it is associated with. */
  rotation?: number
  /** Visibility range, set only when it had to grow to reach the associated device. */
  distance?: number
}

interface GetCompanionPlacementsArgsI {
  devices: DeviceI[]
  draggedDeviceId: string
  dropPosition: Point
  iconSize: number
  mapImageSize: Size
}

/** A slice of a circle to spread companions across, in maths radians (0 = east, clockwise). */
interface FanArcI {
  centreAngleRadians: number
  sweepRadians: number
}

/** Fan radius as a multiple of the current floor plan icon size. */
export const FAN_RADIUS_ICON_MULTIPLIER = 3

/** Extra breathing room between adjacent icons before the fan is allowed to grow. */
export const FAN_MIN_GAP_FACTOR = 1.15

/**
 * Portion of a camera's field of view that companions are spread across, so they sit inside
 * the cone rather than on its edges — `isDeviceInCameraRange` excludes the edges.
 */
export const CONE_SWEEP_USAGE = 0.7

/** Portion of a camera's visibility range that companions are placed at. */
export const CONE_RADIUS_FACTOR = 0.6

/** Headroom applied when a camera's visibility range has to grow to reach a companion. */
export const VIEW_RANGE_SAFETY_FACTOR = 1.25

/** Radius fractions tried, largest first, to keep a cone fan inside the image. */
const RADIUS_FIT_FACTORS = [1, 0.7, 0.45, 0.25]

/** Cameras are the largest icon on the chart, rendered at twice the base icon size. */
const LARGEST_ICON_SIZE_MULTIPLIER = 2

/** Start the fan at 12 o'clock and work clockwise. */
const FAN_START_ANGLE_RADIANS = -Math.PI / 2

/** Offset between the chart's bearing convention (0 = 12 o'clock) and maths angles. */
const BEARING_TO_MATHS_ANGLE_OFFSET = 90

/** How many times a duplicate position is nudged before the overlap is accepted. */
const MAX_NUDGE_ATTEMPTS = 8

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const isValidMapImageSize = (mapImageSize: Size): boolean => {
  const [imageWidth, imageHeight] = mapImageSize || []
  return imageWidth > 0 && imageHeight > 0
}

/**
 * Keeps a whole icon on the image rather than just its centre point, without ever inverting
 * the clamp range on a floor plan smaller than the icons drawn on it.
 */
const getFanMargin = (mapImageSize: Size, iconSize: number): number => {
  const [imageWidth, imageHeight] = mapImageSize
  return Math.min(iconSize, imageWidth / 2, imageHeight / 2)
}

const getPositionKey = ([x, y]: Point): string =>
  `${Math.round(x)},${Math.round(y)}`

/**
 * Clamping can collapse two fan positions onto the same coordinate near an edge or corner,
 * which would leave one icon un-clickable underneath the other. Nudge duplicates towards the
 * centre of the image, where there is always room.
 */
const dedupePositions = (
  positions: Point[],
  mapImageSize: Size,
  iconSize: number
): Point[] => {
  const [imageWidth, imageHeight] = mapImageSize
  const margin = getFanMargin(mapImageSize, iconSize)
  const takenKeys = new Set<string>()

  return positions.map(([x, y]) => {
    const towardsCentreX = x > imageWidth / 2 ? -1 : 1
    const towardsCentreY = y > imageHeight / 2 ? -1 : 1
    let position: Point = [x, y]

    for (
      let attempt = 1;
      takenKeys.has(getPositionKey(position)) && attempt <= MAX_NUDGE_ATTEMPTS;
      attempt++
    ) {
      const offset = attempt * iconSize
      position = [
        clamp(x + offset * towardsCentreX, margin, imageWidth - margin),
        clamp(y + offset * towardsCentreY, margin, imageHeight - margin),
      ]
    }

    takenKeys.add(getPositionKey(position))
    return position
  })
}

/**
 * IDs of the devices directly associated with the given device, one hop only.
 *
 * Works from either side of an association by branching on whether the device is the
 * connection's door: a door/alarm's counterparts are the connection's cameras, whereas a
 * camera's counterpart is the door. Sibling cameras hanging off the same door are two hops
 * away (camera -> door -> camera) and are deliberately excluded.
 */
export const getDirectlyAssociatedDeviceIds = (device: DeviceI): string[] => {
  const associatedIds = new Set<string>()

  device?.tailgateConnections?.forEach((connection) => {
    if (!connection?.door) return

    if (connection.door === device.id) {
      connection.cameraIds?.forEach((cameraId) => {
        if (cameraId && cameraId !== device.id) {
          associatedIds.add(cameraId)
        }
      })
      return
    }

    associatedIds.add(connection.door)
  })

  return [...associatedIds]
}

/**
 * Associated devices that should be pulled onto the floor plan alongside the dragged device.
 *
 * Only devices present in the current `devices` list are eligible. Associated devices on
 * another floor, or filtered out of the panel, are skipped: we know their IDs but not their
 * floor or placed state, and persisting a position for them could re-home a device that is
 * already placed on a different floor. Order follows `devices` (the query sorts by name),
 * so the resulting fan is deterministic.
 */
export const getUnplacedCompanionIds = (
  devices: DeviceI[],
  draggedDeviceId: string
): string[] => {
  const draggedDevice = devices?.find((device) => device.id === draggedDeviceId)
  if (!draggedDevice) return []

  const associatedIds = new Set(getDirectlyAssociatedDeviceIds(draggedDevice))
  if (associatedIds.size === 0) return []

  return devices
    .filter(
      (device) =>
        device.id !== draggedDeviceId &&
        !device.placed &&
        associatedIds.has(device.id)
    )
    .map((device) => device.id)
}

/**
 * Holds the radius at `FAN_RADIUS_ICON_MULTIPLIER` icons for small fans, growing it only once
 * the icons would start to overlap each other around the circumference.
 */
export const getFanRadius = (
  iconSize: number,
  companionCount: number
): number => {
  const baseRadius = FAN_RADIUS_ICON_MULTIPLIER * iconSize
  if (companionCount < 2) return baseRadius

  const iconFootprint =
    iconSize * LARGEST_ICON_SIZE_MULTIPLIER * FAN_MIN_GAP_FACTOR
  const crowdFreeRadius = (companionCount * iconFootprint) / (2 * Math.PI)

  return Math.max(baseRadius, crowdFreeRadius)
}

/**
 * Angles the companions are spread across, in maths radians. Without an arc the fan is a full
 * circle starting at 12 o'clock; with one it is confined to that slice, and a single companion
 * sits dead centre of it.
 */
const getFanAngles = (count: number, arc?: FanArcI): number[] => {
  if (!arc) {
    const angleStep = (2 * Math.PI) / count
    return Array.from(
      { length: count },
      (_, index) => FAN_START_ANGLE_RADIANS + index * angleStep
    )
  }

  if (count === 1) return [arc.centreAngleRadians]

  const angleStep = arc.sweepRadians / (count - 1)
  return Array.from(
    { length: count },
    (_, index) =>
      arc.centreAngleRadians - arc.sweepRadians / 2 + index * angleStep
  )
}

const getUnclampedPosition = (
  origin: Point,
  radius: number,
  angleRadians: number
): Point => [
  origin[0] + radius * Math.cos(angleRadians),
  origin[1] + radius * Math.sin(angleRadians),
]

/**
 * Bearing from one point to another in the chart's convention (0 = 12 o'clock, clockwise),
 * matching how `isDeviceInCameraRange` derives a device's angle.
 */
export const getBearingBetween = (from: Point, to: Point): number =>
  translateAngleTo360Range(
    geometric.lineAngle([from, to]) + BEARING_TO_MATHS_ANGLE_OFFSET
  )

/** The slice of a camera's field of view that companions are placed within. */
const getCameraViewArc = (camera: DeviceI): FanArcI => ({
  centreAngleRadians: geometric.angleToRadians(
    camera.rotation - BEARING_TO_MATHS_ANGLE_OFFSET
  ),
  sweepRadians: geometric.angleToRadians(camera.angle) * CONE_SWEEP_USAGE,
})

/**
 * A camera's visibility range, grown just enough to reach the device it is being aimed at.
 * `isDeviceInCameraRange` compares with a strict `>`, hence the headroom. Never shrinks a
 * range the user has already configured.
 */
const getViewRangeReaching = (
  camera: DeviceI,
  cameraPosition: Point,
  target: Point
): number => {
  const gap = geometric.lineLength([cameraPosition, target])
  return Math.max(camera?.distance || 0, gap * VIEW_RANGE_SAFETY_FACTOR)
}

/**
 * Largest of the candidate radii whose whole fan lands inside the image without clamping, so a
 * cone fan near an edge tightens instead of having positions pulled out of the cone.
 */
const getFittedRadius = (
  origin: Point,
  radius: number,
  angles: number[],
  mapImageSize: Size,
  iconSize: number
): number => {
  const [imageWidth, imageHeight] = mapImageSize
  const margin = getFanMargin(mapImageSize, iconSize)
  const isInBounds = (candidate: number) =>
    angles.every((angle) => {
      const [x, y] = getUnclampedPosition(origin, candidate, angle)
      return (
        x >= margin &&
        y >= margin &&
        x <= imageWidth - margin &&
        y <= imageHeight - margin
      )
    })

  const candidates = RADIUS_FIT_FACTORS.map((factor) => radius * factor)
  return candidates.find(isInBounds) ?? candidates[candidates.length - 1]
}

/**
 * Evenly spaced positions around `origin`, clamped inside the floor plan image so every
 * position satisfies `validateMapPosition`, then deduplicated.
 *
 * `origin`, `radius`, `iconSize` and `mapImageSize` are all in map group user units, so the
 * radius is zoom invariant and directly comparable to the image bounds.
 */
export const getFanPositions = (
  origin: Point,
  count: number,
  radius: number,
  mapImageSize: Size,
  iconSize: number,
  arc?: FanArcI
): Point[] => {
  if (count <= 0 || !isValidMapImageSize(mapImageSize)) return []

  const [imageWidth, imageHeight] = mapImageSize
  const margin = getFanMargin(mapImageSize, iconSize)

  const positions: Point[] = getFanAngles(count, arc).map((angle) => {
    const [x, y] = getUnclampedPosition(origin, radius, angle)
    return [
      clamp(x, margin, imageWidth - margin),
      clamp(y, margin, imageHeight - margin),
    ]
  })

  return dedupePositions(positions, mapImageSize, iconSize)
}

/**
 * Resolves which associated devices come along with a dropped device, where they land, and
 * how they are aimed. Returns an empty list (never throws) when there is nothing to place.
 *
 * Both directions leave the companion inside a view cone, which is what makes the association
 * legible on the floor plan:
 * - dropping a door/alarm fans its cameras around it and turns each one to face it
 * - dropping a camera places its doors/alarms inside that camera's own cone, since a door has
 *   no cone of its own to aim
 *
 * Keeping every position on the image takes priority: if the drop point is too close to an
 * edge for the fan to fit even at its tightest, positions are clamped and the cone is no
 * longer guaranteed.
 */
export const getCompanionPlacements = ({
  devices,
  draggedDeviceId,
  dropPosition,
  iconSize,
  mapImageSize,
}: GetCompanionPlacementsArgsI): CompanionPlacementI[] => {
  if (!isValidMapImageSize(mapImageSize)) return []

  const draggedDevice = devices?.find((device) => device.id === draggedDeviceId)
  const companionIds = getUnplacedCompanionIds(devices, draggedDeviceId)
  if (companionIds.length === 0) return []

  const baseRadius = getFanRadius(iconSize, companionIds.length)

  if (draggedDevice.deviceType === DeviceType.Camera) {
    const arc = getCameraViewArc(draggedDevice)
    const coneRadius = Math.min(
      baseRadius,
      draggedDevice.distance * CONE_RADIUS_FACTOR
    )
    const radius = getFittedRadius(
      dropPosition,
      coneRadius,
      getFanAngles(companionIds.length, arc),
      mapImageSize,
      iconSize
    )
    const positions = getFanPositions(
      dropPosition,
      companionIds.length,
      radius,
      mapImageSize,
      iconSize,
      arc
    )

    return companionIds.map((deviceId, index) => {
      const [x, y] = positions[index]
      return { deviceId, x, y }
    })
  }

  const positions = getFanPositions(
    dropPosition,
    companionIds.length,
    baseRadius,
    mapImageSize,
    iconSize
  )

  return companionIds.map((deviceId, index) => {
    const [x, y] = positions[index]
    const companion = devices.find((device) => device.id === deviceId)
    if (companion?.deviceType !== DeviceType.Camera) {
      return { deviceId, x, y }
    }

    return {
      deviceId,
      x,
      y,
      rotation: getBearingBetween([x, y], dropPosition),
      distance: getViewRangeReaching(companion, [x, y], dropPosition),
    }
  })
}
